import type { ThemeConfig } from './types'

const densitySpacing = {
  compact: {
    xs: '0.25rem',
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.65rem',
    xl: '0.85rem',
    '2xl': '1rem',
    sectionGap: '0.75rem',
    cardPadding: '0.75rem',
    pagePadding: '1rem',
  },
  comfortable: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '0.75rem',
    lg: '1rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    sectionGap: '1rem',
    cardPadding: '1rem',
    pagePadding: '1.5rem',
  },
  spacious: {
    xs: '0.375rem',
    sm: '0.625rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.75rem',
    '2xl': '2rem',
    sectionGap: '1.5rem',
    cardPadding: '1.25rem',
    pagePadding: '2rem',
  },
}

/**
 * Apply all theme CSS custom properties to :root.
 * Called from ThemeProvider's useEffect on every theme change.
 */
export function applyThemeVars(config: ThemeConfig) {
  const c = config.colors
  const s = config.style
  const t = config.typography
  const ds = densitySpacing[s.density]
  const root = document.documentElement.style

  // Colors
  root.setProperty('--primary', c.primary)
  root.setProperty('--primary-hover', c.primaryHover)
  root.setProperty('--primary-active', c.primaryActive)
  root.setProperty('--primary-foreground', '#ffffff')
  root.setProperty('--accent', c.accent)
  root.setProperty('--accent-hover', c.accentHover)
  root.setProperty('--background', c.background)
  root.setProperty('--background-secondary', c.backgroundSecondary)
  root.setProperty('--foreground', c.text)
  root.setProperty('--foreground-muted', c.textMuted)
  root.setProperty('--border', c.border)
  root.setProperty('--error', c.error)
  root.setProperty('--success', c.success)
  root.setProperty('--warning', c.warning)
  root.setProperty('--secondary', c.secondary || '#8B5A2B')
  root.setProperty('--secondary-hover', c.secondaryHover || '#7A4E25')
  root.setProperty('--sidebar-bg', c.sidebarBackground)
  root.setProperty('--sidebar-text', c.sidebarText)
  root.setProperty('--sidebar-text-muted', c.sidebarTextMuted)
  root.setProperty('--sidebar-active-bg', c.sidebarActiveBackground)
  root.setProperty('--sidebar-active-border', c.sidebarActiveBorder)
  root.setProperty('--accent-b2c', c.accentB2C || '#E85D2A')
  root.setProperty('--accent-schools', c.accentSchools || '#F4C95D')
  root.setProperty('--accent-providers', c.accentProviders || '#4B8B9B')

  // Typography
  root.setProperty('--font-body', t.fontFamily)
  root.setProperty('--font-heading', t.fontFamilyHeading)
  root.setProperty('--font-mono', t.fontFamilyMono)

  // Card
  root.setProperty('--theme-card-shadow', s.cardShadow)
  root.setProperty('--theme-card-border-width', s.cardBorderWidth)
  root.setProperty('--theme-card-radius', s.cardBorderRadius)
  // Headings
  root.setProperty('--theme-heading-weight', s.headingWeight)
  root.setProperty('--theme-heading-letter-spacing', s.headingLetterSpacing)
  root.setProperty('--theme-heading-text-transform', s.headingTextTransform)
  // Buttons
  root.setProperty('--theme-button-radius', s.buttonRadius)
  root.setProperty('--theme-button-weight', s.buttonWeight)
  // Sidebar / nav
  root.setProperty('--theme-sidebar-width', s.sidebarWidth)
  root.setProperty('--theme-nav-item-radius', s.navItemRadius)
  root.setProperty('--theme-nav-item-border-width', s.navItemBorderWidth)
  // Shadows
  root.setProperty('--theme-shadow-sm', s.surfaceShadowSm)
  root.setProperty('--theme-shadow-md', s.surfaceShadowMd)
  root.setProperty('--theme-shadow-lg', s.surfaceShadowLg)
  // Borders
  root.setProperty('--theme-border-width', s.borderWidth)
  root.setProperty('--theme-divider-style', s.dividerStyle)
  // Motion
  root.setProperty('--theme-transition', s.transitionSpeed)
  // Background
  root.setProperty('--theme-bg-texture', s.backgroundTexture)
  // Badge
  root.setProperty('--theme-badge-radius', s.badgeRadius)
  // Input
  root.setProperty('--theme-input-radius', s.inputRadius)
  root.setProperty('--theme-input-border-width', s.inputBorderWidth)
  // Density spacing
  root.setProperty('--theme-space-xs', ds.xs)
  root.setProperty('--theme-space-sm', ds.sm)
  root.setProperty('--theme-space-md', ds.md)
  root.setProperty('--theme-space-lg', ds.lg)
  root.setProperty('--theme-space-xl', ds.xl)
  root.setProperty('--theme-space-2xl', ds['2xl'])
  root.setProperty('--theme-section-gap', ds.sectionGap)
  root.setProperty('--theme-card-padding', ds.cardPadding)
  root.setProperty('--theme-page-padding', ds.pagePadding)
  // Layout structure
  root.setProperty('--theme-content-max-width', s.contentMaxWidth)
  root.setProperty('--theme-card-gap', s.cardGap)
  // Structural effects
  root.setProperty('--theme-sidebar-inset', s.sidebarInset)
  root.setProperty('--theme-sidebar-radius', s.sidebarRadius)
  root.setProperty('--theme-card-accent-border', s.cardAccentBorder)
  root.setProperty('--theme-card-bg', s.cardBackground)
  root.setProperty('--theme-card-backdrop-blur', s.cardBackdropBlur)
  root.setProperty('--theme-heading-border-bottom', s.headingBorderBottom)
  // Layout
  const l = config.layout
  root.setProperty('--theme-topnav-height', l.topNavHeight)
  document.documentElement.dataset.navMode = l.navigationMode
}
